import { SPEICHER_SPERRE_ANTEIL, SPEICHER_WARNUNG_ANTEIL } from '@uep/shared';

/**
 * Speicherbudget (PHASENPLAN 1.7).
 *
 * Ein volles IndexedDB fällt sonst erst dann auf, wenn ein Foto verlorengeht —
 * deshalb wird früh gewarnt und ab 95 % hart gesperrt.
 */

export class SpeicherGesperrtFehler extends Error {
  constructor(meldung: string) {
    super(meldung);
    this.name = 'SpeicherGesperrtFehler';
  }
}

export interface SpeicherStand {
  belegtBytes: number;
  kontingentBytes: number;
  anteil: number;
  warnung: boolean;
  gesperrt: boolean;
  /** false, wenn der Browser keine Schätzung liefert (dann keine Sperre). */
  messbar: boolean;
}

export const pruefeSpeicher = async (): Promise<SpeicherStand> => {
  const leer: SpeicherStand = {
    belegtBytes: 0,
    kontingentBytes: 0,
    anteil: 0,
    warnung: false,
    gesperrt: false,
    messbar: false,
  };

  if (typeof navigator === 'undefined' || !navigator.storage?.estimate) return leer;

  try {
    const schaetzung = await navigator.storage.estimate();
    const belegt = schaetzung.usage ?? 0;
    const kontingent = schaetzung.quota ?? 0;
    if (kontingent <= 0) return { ...leer, belegtBytes: belegt };

    const anteil = belegt / kontingent;
    return {
      belegtBytes: belegt,
      kontingentBytes: kontingent,
      anteil,
      warnung: anteil >= SPEICHER_WARNUNG_ANTEIL,
      gesperrt: anteil >= SPEICHER_SPERRE_ANTEIL,
      messbar: true,
    };
  } catch {
    return leer;
  }
};

/**
 * Bittet um dauerhaften Speicher. Ohne diese Zusage darf der Browser
 * IndexedDB unter Druck einfach leeren — bei Protokolldaten wäre das fatal.
 */
export const dauerhaftenSpeicherAnfordern = async (): Promise<boolean> => {
  if (typeof navigator === 'undefined' || !navigator.storage?.persist) return false;
  try {
    if (await navigator.storage.persisted()) return true;
    return await navigator.storage.persist();
  } catch {
    return false;
  }
};

export const speicherText = (stand: SpeicherStand): string => {
  if (!stand.messbar) return 'Speicherbelegung unbekannt';
  const prozent = Math.round(stand.anteil * 100);
  const belegtMb = Math.round(stand.belegtBytes / (1024 * 1024));
  const gesamtMb = Math.round(stand.kontingentBytes / (1024 * 1024));
  return `${prozent} % belegt (${belegtMb} von ${gesamtMb} MB)`;
};
